// Phase 15 Plan 05 Task 3 — Net wrappers for the friends surface (Phase 7 backend).
//
// All endpoints already exist in openapi.yaml and ship in Phase 7. This
// module is a thin typed front-door so the friends UI (Plan 07/08) reads a
// single import boundary.
//
// Endpoints used:
//   - POST /api/friends/request         → send friend request (by 8-char code)
//   - POST /api/friends/accept          → accept pending inbound
//   - POST /api/friends/reject          → reject pending inbound
//   - GET  /api/friends                 → accepted friendships
//   - GET  /api/friends/requests        → pending inbound inbox
//   - POST /api/friends/match           → create friend match (Phase 11)

import type { components, operations } from '@runegambit/api-types'
import { apiFetch } from './client'
import { createIdempotencyKey } from './idempotency'

// ──────────────────────────────────────────────────────────────────────
// Response & request shapes — derived from the generated OpenAPI types.
// ──────────────────────────────────────────────────────────────────────

export type FriendMatchConfig = components['schemas']['FriendMatchConfig']

/**
 * GET /api/friends 200 body — caller's accepted friendships.
 */
export type FriendsListResponse =
  operations['friendsList']['responses'][200]['content']['application/vnd.runegambit.v1+json']

/**
 * GET /api/friends/requests 200 body — pending inbound inbox.
 */
export type FriendRequestInboxResponse =
  operations['friendRequestInbox']['responses'][200]['content']['application/vnd.runegambit.v1+json']

/**
 * GET /api/friends/requests/outbound 200 body — sender's pending outbox.
 */
export type FriendRequestOutboxResponse =
  operations['friendRequestOutbox']['responses'][200]['content']['application/vnd.runegambit.v1+json']

export interface SendFriendRequestResponse {
  readonly request_id: string
}

export interface AcceptRejectResponse {
  readonly ok: true
}

export interface CancelFriendRequestResponse {
  readonly cancelled: true
}

export interface RemoveFriendResponse {
  readonly removed: boolean
}

export interface SendFriendMatchInviteResponse {
  readonly matchId: string
  readonly inviteId: string
}

// ──────────────────────────────────────────────────────────────────────
// Reads
// ──────────────────────────────────────────────────────────────────────

export async function fetchFriendsList(): Promise<FriendsListResponse> {
  return apiFetch<FriendsListResponse>('/api/friends', 'GET')
}

export async function fetchFriendsInbox(): Promise<FriendRequestInboxResponse> {
  return apiFetch<FriendRequestInboxResponse>('/api/friends/requests', 'GET')
}

export async function fetchFriendsOutbox(): Promise<FriendRequestOutboxResponse> {
  return apiFetch<FriendRequestOutboxResponse>(
    '/api/friends/requests/outbound',
    'GET',
  )
}

// ──────────────────────────────────────────────────────────────────────
// Writes — all carry a fresh UUIDv4 Idempotency-Key per call (FIFO-50
// cache safe; on 5xx, apiFetch retries once with the SAME key).
// ──────────────────────────────────────────────────────────────────────

/**
 * POST /api/friends/request — send a friend request by 8-char friend code.
 *
 * On unknown / self-addressed / already-friends → 404 byte-equal (oracle-safe;
 * no details). Caller MUST surface a generic "couldn't add" toast.
 */
export async function sendFriendRequest(
  friend_code: string,
): Promise<SendFriendRequestResponse> {
  return apiFetch<SendFriendRequestResponse>('/api/friends/request', 'POST', {
    body: { friend_code },
    idempotencyKey: createIdempotencyKey(),
  })
}

/**
 * POST /api/friends/accept — accept a pending inbound request.
 */
export async function acceptFriendRequest(
  request_id: string,
): Promise<AcceptRejectResponse> {
  return apiFetch<AcceptRejectResponse>('/api/friends/accept', 'POST', {
    body: { request_id },
    idempotencyKey: createIdempotencyKey(),
  })
}

/**
 * POST /api/friends/reject — reject a pending inbound request.
 */
export async function rejectFriendRequest(
  request_id: string,
): Promise<AcceptRejectResponse> {
  return apiFetch<AcceptRejectResponse>('/api/friends/reject', 'POST', {
    body: { request_id },
    idempotencyKey: createIdempotencyKey(),
  })
}

/**
 * POST /api/friends/cancel — sender retracts a pending outbound request.
 * 409 on already-resolved (idempotent: caller treats it as already gone).
 */
export async function cancelOutboundFriendRequest(
  request_id: string,
): Promise<CancelFriendRequestResponse> {
  return apiFetch<CancelFriendRequestResponse>('/api/friends/cancel', 'POST', {
    body: { request_id },
    idempotencyKey: createIdempotencyKey(),
  })
}

/**
 * POST /api/friends/remove — un-friend a specific user.
 *
 * Self-scoped server-side: the canonical (LEAST, GREATEST) DELETE can only
 * touch a row the caller is part of. Re-adding works via the normal
 * `sendFriendRequest` flow afterwards (duplicate-pending guard only blocks
 * status='pending', so a deleted friendship doesn't block a new request).
 *
 * 404 collapses all "couldn't remove" cases (malformed uuid, self-target,
 * not-friends) into a single oracle. Caller surfaces a generic toast.
 */
export async function removeFriend(
  friend_uuid: string,
): Promise<RemoveFriendResponse> {
  return apiFetch<RemoveFriendResponse>('/api/friends/remove', 'POST', {
    body: { friend_uuid },
    idempotencyKey: createIdempotencyKey(),
  })
}

/**
 * POST /api/friends/match — create a friend match invite.
 *
 * On bad code / self / non-friend → 404 byte-equal (oracle-safe).
 * On 409 → active-match cap exceeded (DEC-009).
 */
export async function sendFriendMatchInvite(
  friend_code: string,
  config: FriendMatchConfig,
): Promise<SendFriendMatchInviteResponse> {
  return apiFetch<SendFriendMatchInviteResponse>('/api/friends/match', 'POST', {
    body: { friend_code, config },
    idempotencyKey: createIdempotencyKey(),
  })
}
